import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Entity } from './types';
import type { ILogging } from '../../../../api/types';
import { isNil, isNotNil } from '../../../../utils/isNotNil';
import { useScreenComponentLoadMore } from './useScreenComponentLoadMore';

interface UseGoodsCarouselLoadMoreParams {
  screenName?: string;
  componentId?: number;
  screenLogging?: ILogging;
  /** 부모 props body.item.next_token — 초기 토큰 및 리셋 트리거 */
  initialNextToken?: string | null;
  /** 현재 선택된 radio/subRadio 필터 쿼리 */
  filterParams?: Record<string, string | null>;
  /** PDP 임베드 시 goods_sno 등 추가 쿼리 */
  extraParams?: Record<string, string | null>;
}

/** null 값을 제거하고 여러 쿼리 맵을 단일 string 맵으로 합친다. */
const toQueryParams = (
  ...maps: Array<Record<string, string | null> | undefined>
): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const map of maps) {
    if (!map) continue;
    for (const [key, value] of Object.entries(map)) {
      if (isNotNil(value)) result[key] = value;
    }
  }
  return result;
};

/**
 * 상품 캐러셀 load-more 상태(itemList/nextToken/isLoadingMore)를 컴포넌트 로컬로
 * 관리한다. 메인 AtomicRenderer와 PDP GoodsAtomicRenderer 양쪽에서 상위 배선
 * 없이 동작한다.
 */
export const useGoodsCarouselLoadMore = ({
  screenName,
  componentId,
  screenLogging,
  initialNextToken,
  filterParams,
  extraParams,
}: UseGoodsCarouselLoadMoreParams) => {
  const [appendedEntity, setAppendedEntity] = useState<Entity[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(
    initialNextToken ?? null,
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // in-flight 가드는 동기적으로 평가돼야 하므로 ref로 별도 추적
  const isLoadingRef = useRef(false);
  // 필터 변경/리셋마다 증가하는 세대 값. in-flight 요청이 완료될 때 세대가
  // 바뀌었으면 이전 필터의 결과이므로 폐기한다.
  const generationRef = useRef(0);

  const { mutateAsync } = useScreenComponentLoadMore();

  // loadMore 정체성을 useCallback([])로 고정하기 위해 변하는 값은 ref로 읽는다.
  const nextTokenRef = useRef(nextToken);
  nextTokenRef.current = nextToken;
  const latestRef = useRef({
    screenName,
    componentId,
    screenLogging,
    filterParams,
    extraParams,
    mutateAsync,
  });
  latestRef.current = {
    screenName,
    componentId,
    screenLogging,
    filterParams,
    extraParams,
    mutateAsync,
  };

  // 필터가 동일 token을 반복해도 리셋되도록 안정 시그니처로 비교한다.
  const filterParamsKey = useMemo(
    () => JSON.stringify(filterParams ?? {}),
    [filterParams],
  );

  // 부모 데이터(필터 변경/리로드 등)로 entity가 교체되면 append 상태를 초기화한다.
  useEffect(() => {
    generationRef.current += 1;
    setAppendedEntity([]);
    setNextToken(initialNextToken ?? null);
    isLoadingRef.current = false;
    setIsLoadingMore(false);
  }, [componentId, initialNextToken, filterParamsKey]);

  const loadMore = useCallback(async () => {
    if (isLoadingRef.current) return;
    const { screenName, componentId, screenLogging, filterParams, extraParams } =
      latestRef.current;
    const token = nextTokenRef.current;
    if (!token || !screenName || isNil(componentId)) return;

    isLoadingRef.current = true;
    setIsLoadingMore(true);
    const generation = generationRef.current;
    try {
      const converted = await latestRef.current.mutateAsync({
        screenName,
        componentId,
        screenLogging,
        queryParams: toQueryParams(
          { next_token: token },
          filterParams,
          extraParams,
        ),
      });
      // await 사이에 필터 변경/리셋이 일어났으면 이전 필터의 결과이므로 폐기한다.
      if (generation !== generationRef.current) return;
      const newItems = converted.entity.itemList;
      if (!newItems || newItems.length === 0) {
        // 매핑 실패/빈 페이지 — 더 가져올 수 없으므로 종료
        setNextToken(null);
        return;
      }
      setAppendedEntity(prev => [...prev, ...newItems]);
      const responseToken = converted.entity.body?.item?.next_token ?? null;
      // 동일 token이 반복되면 무한 루프 방지를 위해 종료
      setNextToken(responseToken === token ? null : responseToken);
    } catch {
      // silent fail — 다음 트리거에서 재시도
    } finally {
      // 리셋 이후 완료된 요청은 새 세대의 로딩 상태를 건드리지 않는다.
      if (generation === generationRef.current) {
        isLoadingRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, []);

  return { appendedEntity, isLoadingMore, loadMore };
};
